import { Controller } from "@hotwired/stimulus";
import L from "leaflet";
import {
  clearLeafletArtifacts,
  containerHasSize,
  watchContainerSize,
} from "./utils/map_container";
import { pinDivIcon } from "./utils/map_markers";
import { textPopup } from "./utils/map_popups";

// Fix for default marker icons in Leaflet when using bundlers
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

export default class extends Controller {
  static values = {
    latitude: Number,
    longitude: Number,
    venueName: String,
    interactive: { type: Boolean, default: true },
    // For dual location mode (delivery route)
    destinationLatitude: Number,
    destinationLongitude: Number,
    destinationName: String,
    showRoute: { type: Boolean, default: false },
    // Translatable text
    deliveringFromText: { type: String, default: "Delivering from" },
    deliveringToText: { type: String, default: "Delivering to" },
    // Delivery radius
    deliveryRadius: { type: Number, default: 0 },
    distanceUnit: { type: String, default: "km" },
    // Allow the destination marker to be dragged to
    // fine-tune the drop-off point. Independent of
    // `interactive` so we can keep the rest of the
    // map locked (no zoom/scroll) while still
    // letting the user nudge the pin.
    destinationDraggable: { type: Boolean, default: false },
    // The drop-off pin's tone. TWO surfaces mount this
    // controller with a "B" pin, and both pass "ink"
    // explicitly now: the delivery checkout (CAL-484) and
    // the order-tracking route map (CAL-485). The third
    // consumer, the admin venue editor, is single-marker
    // origin mode and draws no drop-off at all, so this
    // default reaches nothing in `app/`. It stays accent
    // rather than following them because a caller that
    // names no tone is asking for the generic drop-off
    // role, and changing what that means under it is a
    // decision for whoever adds one.
    destinationTone: { type: String, default: "accent" },
    // Same idea as `destinationDraggable`, but for
    // the single-marker (origin) mode used by the
    // admin venue location editor. When true, the
    // venue marker is draggable and emits a
    // `location-map:originDragged` event on dragend
    // with the new lat/lng.
    originDraggable: { type: Boolean, default: false },
    // Open centred on the DESTINATION at this zoom
    // rather than fitting both ends of the route
    // (CAL-409). Zero means "fit the route", which is
    // what every read-only route map wants.
    //
    // The pin-adjust map at checkout wants the
    // opposite: it exists to let the customer place a
    // pin on their own building, and a view wide
    // enough to hold the restaurant as well is a view
    // too wide to see a doorway on.
    destinationZoom: { type: Number, default: 0 },
  };

  connect() {
    // A Turbo morph can preserve this container node
    // across a render, so the element Stimulus reconnects
    // us to is the SAME node Leaflet already stamped with
    // `_leaflet_id`. And `entrypoints/users.js` drives
    // disconnect/connect by hand on morph, on top of
    // Stimulus's own lifecycle, so connect() can also run
    // twice with no disconnect between. Leaflet refuses to
    // build a map on a stamped container ("Map container
    // is already initialized."), and that throw escaped
    // into Turbo's render callback and stranded the whole
    // visit (CAL-401). Tear down first so init is
    // idempotent no matter how we got here.
    this.teardownMap();
    this.initializeMap();
  }

  // Value change callbacks
  destinationLatitudeValueChanged() {
    if (this.suppressUpdate) return;
    if (this.mapReady && this.showRouteValue) {
      this.updateMap();
    }
  }

  destinationLongitudeValueChanged() {
    if (this.suppressUpdate) return;
    if (this.mapReady && this.showRouteValue) {
      this.updateMap();
    }
  }

  // Single-marker mode: the admin "Reset to address"
  // button overwrites latitudeValue/longitudeValue
  // to snap the pin back to the geocoded coords.
  // Re-render in place so the user sees the snap.
  latitudeValueChanged() {
    if (this.suppressUpdate) return;
    if (this.mapReady && !this.showRouteValue) {
      this.updateMap();
    }
  }

  longitudeValueChanged() {
    if (this.suppressUpdate) return;
    if (this.mapReady && !this.showRouteValue) {
      this.updateMap();
    }
  }

  showRouteValueChanged() {
    if (this.mapReady) {
      this.updateMap();
    }
  }

  updateMap() {
    // Clear existing layers except the tile layer
    this.map.eachLayer((layer) => {
      if (!(layer instanceof L.TileLayer)) {
        this.map.removeLayer(layer);
      }
    });

    // Re-draw the map content
    if (this.showRouteValue && this.destinationLatitudeValue && this.destinationLongitudeValue) {
      this.showDeliveryRoute();
    } else if (this.latitudeValue && this.longitudeValue) {
      this.showSingleLocation();
    }
  }

  showSingleLocation() {
    this.map.setView([this.latitudeValue, this.longitudeValue], 16);

    // The same teardrop as route mode (no letter:
    // there's only one marker, so A/B labeling would be
    // noise). Primary so it matches the venue marker in
    // route mode.
    const icon = pinDivIcon({
      letter: "",
      tone: "primary",
      draggable: this.originDraggableValue,
    });

    const marker = L.marker(
      [this.latitudeValue, this.longitudeValue],
      {
        icon,
        draggable: this.originDraggableValue,
        autoPan: this.originDraggableValue,
      }
    ).addTo(this.map);

    if (this.originDraggableValue) {
      marker.on("dragend", (event) => {
        const { lat, lng } = event.target.getLatLng();
        // Skip the auto re-render: the marker is
        // already at lat/lng on the canvas.
        this.suppressUpdate = true;
        this.latitudeValue = lat;
        this.longitudeValue = lng;
        this.suppressUpdate = false;
        this.dispatch("originDragged", {
          detail: { latitude: lat, longitude: lng },
          prefix: "location-map",
        });
      });
    }

    // Add popup with venue name. As nodes, never a
    // string: see `textPopup` (CAL-522).
    if (this.venueNameValue) {
      marker.bindPopup(
        textPopup({ text: this.venueNameValue })
      );
      // Don't auto-open the popup when the marker
      // is draggable: it gets in the way of the
      // drag affordance and the user can click to
      // open it themselves.
      if (!this.originDraggableValue) {
        marker.openPopup();
      }
    }

    // Add delivery radius circle if available
    this.addDeliveryRadiusCircle();
  }

  initializeMap() {
    this.mapReady = false;

    // Map options based on whether interaction is enabled
    const mapOptions = {
      dragging: this.interactiveValue,
      touchZoom: this.interactiveValue,
      doubleClickZoom: this.interactiveValue,
      // Plain wheel scroll always belongs to the PAGE,
      // even on an interactive map (CAL-409). This one
      // sits in the middle of the checkout, and a
      // customer scrolling down to the place-order
      // button would otherwise zoom the map instead of
      // reaching it. Ctrl/⌘ + wheel zooms, the same
      // gesture an embedded Google map asks for.
      scrollWheelZoom: false,
      boxZoom: this.interactiveValue,
      keyboard: this.interactiveValue,
      zoomControl: this.interactiveValue,
    };

    // Check if we have valid coordinates
    const hasOriginCoordinates = this.latitudeValue && this.longitudeValue;
    const hasDestinationCoordinates =
      this.destinationLatitudeValue && this.destinationLongitudeValue;
    const isRouteMode =
      this.showRouteValue && hasOriginCoordinates && hasDestinationCoordinates;

    // Initialize map
    this.map = L.map(this.element, mapOptions);

    // Add OpenStreetMap tile layer
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(this.map);

    // A container with no box (`hidden`, a closed sheet,
    // an unselected tab) measures 0x0, and every view
    // decision below is made against that. `fitBounds`
    // then resolves to the tile layer's max zoom, and a
    // later `invalidateSize()` does NOT walk it back: it
    // re-measures the canvas, never the view. Remember we
    // computed blind so `handleContainerResize` can redraw
    // once the container has real dimensions.
    this.viewComputedBlind = !containerHasSize(this.element);

    if (isRouteMode) {
      // Route mode - show both locations and path
      this.showDeliveryRoute();
    } else if (hasOriginCoordinates) {
      // Single location mode
      this.showSingleLocation();
    } else {
      // No coordinates - show world view
      this.map.setView([20, 0], 2);
    }

    // Fix map rendering issues that can occur with dynamic
    // content. Tracked so `teardownMap` can cancel it: a
    // disconnect or a morph-driven re-init inside this
    // window would otherwise fire it against a map that is
    // already gone.
    this.invalidateSizeTimeout = setTimeout(() => {
      this.map.invalidateSize();
    }, 100);

    this.observeContainerSize();

    if (this.interactiveValue) {
      this.enableModifierWheelZoom();
    }

    this.mapReady = true;
  }

  // Watching the container itself rather than listening
  // for the reveal event covers every version of this: a
  // card revealed from `hidden`, a sheet opening, a tab
  // becoming current, an accordion expanding. None of
  // them has to know a map is inside.
  observeContainerSize() {
    this.resizeObserver = watchContainerSize(
      this.element,
      () => this.handleContainerResize()
    );
  }

  handleContainerResize() {
    if (!this.map) return;

    this.map.invalidateSize();

    // `invalidateSize` re-measures the canvas, not the
    // view. A `fitBounds` that ran against a 0x0 viewport
    // sits at the tile layer's max zoom and stays there,
    // so the draw has to be re-run now that there is a
    // real viewport to fit to. Once only: a later
    // hide/show must not throw away a pan or zoom the
    // customer chose.
    if (this.viewComputedBlind) {
      this.viewComputedBlind = false;
      this.updateMap();
    }
  }

  /**
   * Ctrl/⌘ + wheel zooms; a bare wheel is left to the
   * page (CAL-409).
   *
   * Handled here rather than by toggling Leaflet's own
   * `scrollWheelZoom` on a modifier: that handler has
   * to be enabled BEFORE the wheel event it should act
   * on, so flipping it from inside the event swallows
   * the first tick and the map feels broken.
   *
   * `preventDefault` is required and is why the
   * listener cannot be passive: ctrl+wheel is also
   * the browser's own page-zoom gesture.
   */
  enableModifierWheelZoom() {
    this.boundWheelZoom = (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      if (!this.map) return;

      event.preventDefault();

      const direction = event.deltaY < 0 ? 1 : -1;
      this.map.setZoomAround(
        this.map.mouseEventToContainerPoint(event),
        this.map.getZoom() + direction
      );
    };

    this.element.addEventListener(
      "wheel",
      this.boundWheelZoom,
      { passive: false }
    );
  }

  showDeliveryRoute() {
    const originLatLng = [this.latitudeValue, this.longitudeValue];
    const destLatLng = [
      this.destinationLatitudeValue,
      this.destinationLongitudeValue,
    ];

    // A teardrop with a letter on its head. The TIP sits
    // on the marker coordinate, so the anchor is precise
    // however far the user zooms in. Total height 40,
    // so iconAnchor.y = 40. Unchanged from the lollipop
    // these replaced, which is why the map's layout does
    // not shift.
    const originIcon = pinDivIcon({
      letter: "A",
      tone: "primary",
    });
    const destIcon = pinDivIcon({
      letter: "B",
      tone: this.destinationToneValue,
      draggable: this.destinationDraggableValue,
    });

    // Add markers
    const originMarker = L.marker(originLatLng, { icon: originIcon }).addTo(
      this.map
    );
    const destMarker = L.marker(destLatLng, {
      icon: destIcon,
      draggable: this.destinationDraggableValue,
      autoPan: this.destinationDraggableValue,
    }).addTo(this.map);

    if (this.destinationDraggableValue) {
      destMarker.on("dragend", (event) => {
        const { lat, lng } = event.target.getLatLng();
        // Skip the auto re-render: the marker is
        // already at lat/lng on the canvas, and a
        // re-render would tear down the curve and
        // re-fit bounds (visible flicker). Just
        // refresh the curve in place.
        this.suppressUpdate = true;
        this.destinationLatitudeValue = lat;
        this.destinationLongitudeValue = lng;
        this.suppressUpdate = false;
        this.redrawRouteOverlay();
        this.dispatch("destinationDragged", {
          detail: { latitude: lat, longitude: lng },
          prefix: "location-map",
        });
      });
    }

    // Add popups. As nodes, never a string: the venue
    // name is a merchant's and the drop-off a customer's,
    // and a string is parsed as HTML (CAL-522).
    if (this.venueNameValue) {
      originMarker.bindPopup(
        textPopup({
          heading: this.deliveringFromTextValue,
          text: this.venueNameValue,
        })
      );
    }
    if (this.destinationNameValue) {
      destMarker.bindPopup(
        textPopup({
          heading: this.deliveringToTextValue,
          text: this.destinationNameValue,
        })
      );
    }

    this.destMarker = destMarker;
    this.routePolyline = this.drawRouteCurve(originLatLng, destLatLng);

    // Add delivery radius circle
    this.addDeliveryRadiusCircle();

    if (this.destinationZoomValue > 0) {
      // Centred on the drop-off, close in. The
      // restaurant marker and the route curve are
      // still drawn (panning or zooming out reaches
      // them), but the view opens where the customer
      // has something to check.
      this.map.setView(
        destLatLng,
        this.destinationZoomValue
      );
      return;
    }

    // Fit map to show both markers with padding
    const bounds = L.latLngBounds([originLatLng, destLatLng]);
    this.map.fitBounds(bounds, { padding: [50, 50] });
  }

  drawRouteCurve(originLatLng, destLatLng) {
    // Calculate control point for quadratic bezier curve
    const midLat = (originLatLng[0] + destLatLng[0]) / 2;
    const midLng = (originLatLng[1] + destLatLng[1]) / 2;

    const latDiff = destLatLng[0] - originLatLng[0];
    const lngDiff = destLatLng[1] - originLatLng[1];
    const offset = 0.15;

    const controlLat = midLat - lngDiff * offset;
    const controlLng = midLng + latDiff * offset;

    const pathOptions = {
      color: "#3b82f6",
      weight: 3,
      opacity: 0.7,
      dashArray: "10, 10",
    };

    const segments = 20;
    const points = [];

    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const lat =
        (1 - t) * (1 - t) * originLatLng[0] +
        2 * (1 - t) * t * controlLat +
        t * t * destLatLng[0];
      const lng =
        (1 - t) * (1 - t) * originLatLng[1] +
        2 * (1 - t) * t * controlLng +
        t * t * destLatLng[1];
      points.push([lat, lng]);
    }

    return L.polyline(points, pathOptions).addTo(this.map);
  }

  redrawRouteOverlay() {
    if (this.routePolyline) {
      this.map.removeLayer(this.routePolyline);
    }
    const originLatLng = [this.latitudeValue, this.longitudeValue];
    const destLatLng = [
      this.destinationLatitudeValue,
      this.destinationLongitudeValue,
    ];
    this.routePolyline = this.drawRouteCurve(originLatLng, destLatLng);
  }

  addDeliveryRadiusCircle() {
    if (!this.deliveryRadiusValue || this.deliveryRadiusValue <= 0) return;
    if (!this.latitudeValue || !this.longitudeValue) return;

    // Convert radius to meters based on unit
    const radiusInMeters =
      this.distanceUnitValue === "km"
        ? this.deliveryRadiusValue * 1000
        : this.deliveryRadiusValue * 1609.34; // miles to meters

    // Create circle with styling
    const circleOptions = {
      color: "#3b82f6", // Blue border
      fillColor: "#3b82f6", // Blue fill
      fillOpacity: 0.1, // Very light fill
      weight: 2, // Border width
      opacity: 0.8, // Border opacity
    };

    L.circle([this.latitudeValue, this.longitudeValue], {
      radius: radiusInMeters,
      ...circleOptions,
    }).addTo(this.map);
  }

  disconnect() {
    this.teardownMap();
  }

  // Returns the container to a state `L.map()` accepts,
  // and the controller to a state a fresh connect can
  // start from. Both steps are needed, and they fail
  // independently:
  //
  // 1. `map.remove()` is the happy path, but it throws
  //    ("Map container is being reused by another
  //    instance") when its container no longer carries the
  //    id it stamped, and it throws BEFORE doing any
  //    cleanup. A throw here is the whole reason CAL-401
  //    poisoned the Turbo visit, so it is swallowed and
  //    step 2 finishes the job.
  // 2. The map may also be pointing at a DIFFERENT node
  //    than `this.element` (morphdom keeps ours, while
  //    the map still holds the one it was built on), or a
  //    previous connect may have thrown before `this.map`
  //    was ever assigned. Either way the live container
  //    can still carry the stamp and the panes from that
  //    init, so clear them off `this.element` directly.
  teardownMap() {
    if (this.invalidateSizeTimeout) {
      clearTimeout(this.invalidateSizeTimeout);
      this.invalidateSizeTimeout = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.viewComputedBlind = false;

    if (this.boundWheelZoom) {
      this.element.removeEventListener(
        "wheel",
        this.boundWheelZoom
      );
      this.boundWheelZoom = null;
    }

    if (this.map) {
      try {
        this.map.remove();
      } catch {
        // Half-dead map: `clearLeafletArtifacts` below
        // still frees the container for a re-init.
      }
    }

    this.map = null;
    this.mapReady = false;
    this.routePolyline = null;

    clearLeafletArtifacts(this.element);
  }
}
